import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Wrench } from 'lucide-react';

const STROKE = 2.75;

// Collapsible "see what I ran" disclosure under an assistant message.
// Renders the list of tool calls + their argument summary + row count
// so the operator can see exactly which database queries produced the
// answer.
function ToolCallTrace({ toolCalls = [], toolResults = [], events = [] }) {
  const [open, setOpen] = useState(false);

  // Merge the in-stream events with the persisted final shape so the
  // trace is identical whether you're viewing a live turn or a
  // reloaded thread.
  const calls = [];
  if (toolCalls && toolCalls.length) {
    toolCalls.forEach(tc => {
      const fn = tc.function || {};
      let args = {};
      try {
        args = typeof fn.arguments === 'string' ? JSON.parse(fn.arguments) : (fn.arguments || {});
      } catch (e) {
        args = { _raw: fn.arguments };
      }
      const matchingResult = (toolResults || []).find(r => r.tool_call_id === tc.id);
      calls.push({
        id: tc.id,
        name: fn.name,
        args,
        result: matchingResult ? matchingResult.result : null,
        rowCount: matchingResult && matchingResult.result
          ? matchingResult.result.row_count
          : null,
      });
    });
  } else if (events && events.length) {
    // Live mode: we may have started events but not yet seen `final`.
    const started = events.filter(e => e.type === 'tool_call_started');
    const finished = events.filter(e => e.type === 'tool_call_finished');
    started.forEach(s => {
      const f = finished.find(x => x.id === s.id);
      calls.push({
        id: s.id,
        name: s.name,
        args: s.arguments || {},
        rowCount: f ? f.row_count : null,
        summary: f ? f.result_summary : 'running…',
      });
    });
  }

  if (!calls.length) return null;

  return (
    <div className="chat-trace">
      <button
        type="button"
        className="chat-trace-toggle"
        onClick={() => setOpen(o => !o)}
      >
        {open ? <ChevronDown size={13} strokeWidth={STROKE} /> : <ChevronRight size={13} strokeWidth={STROKE} />}
        <Wrench size={13} strokeWidth={STROKE} />
        <span>
          {calls.length === 1
            ? '1 tool call'
            : `${calls.length} tool calls`}
        </span>
      </button>
      {open && (
        <div className="chat-trace-body">
          {calls.map((c, idx) => (
            <div className="chat-trace-row" key={c.id || idx}>
              <div className="chat-trace-name">
                <span className="chat-trace-fn">{c.name}</span>
                {c.rowCount != null ? (
                  <span className="tag tag-neutral chat-trace-count">{c.rowCount} rows</span>
                ) : c.summary ? (
                  <span className="tag tag-neutral chat-trace-count">{c.summary}</span>
                ) : null}
              </div>
              <pre className="chat-trace-args">{JSON.stringify(c.args, null, 2)}</pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ToolCallTrace;
